import { Flex } from "@chakra-ui/react";
import {
    useSitecoreContext,
    type ComponentParams,
    type ComponentRendering,
} from "@sitecore-jss/sitecore-jss-nextjs";
import SCText from "commons/sc/SCText";
import CookieConsent, { type CookieConsentFieldProps } from "commons/ui/CookieConsent";
import type { GenericHeaderProps } from "commons/ui/GenericHeader";
import GenericHeader from "commons/ui/GenericHeader";
import Script from "next/script";
import { useRef } from "react";

export type SalesforceFormIntegrationProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: {
        ScriptUrl1: { value: string };
        ScriptUrl2: { value: string };
        Markup: { value: string };
    } & GenericHeaderProps &
        CookieConsentFieldProps;
};

type SalesforceFormIntegrationEditingUIProps = {
    Markup: { value: string };
    ScriptUrl1: { value: string };
    ScriptUrl2: { value: string };
};

const SalesforceFormIntegrationEditingUI = ({
    Markup,
    ScriptUrl1,
    ScriptUrl2,
}: SalesforceFormIntegrationEditingUIProps) => (
    <Flex flexDirection="column" gap="sp16" mb="sp16">
        <Flex flexDirection="column">
            HTML:
            <SCText textField={Markup} border="1px solid black" mt="sp12" />
        </Flex>
        <Flex flexDirection="column">
            Script URL 1:
            <SCText
                textField={ScriptUrl1}
                border="1px solid black"
                mt="sp12"
                wordBreak="break-word"
            />
        </Flex>
        <Flex flexDirection="column">
            Script URL 2:
            <SCText
                textField={ScriptUrl2}
                border="1px solid black"
                mt="sp12"
                wordBreak="break-word"
            />
        </Flex>
    </Flex>
);

const SalesforceFormIntegration = (props: SalesforceFormIntegrationProps) => {
    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();

    const scriptUrl1 = props.fields?.ScriptUrl1?.value;
    const scriptUrl2 = props.fields?.ScriptUrl2?.value;
    const html = props.fields?.Markup?.value;

    const cookieConsentFields = {
        AnalyticsCookies: props.fields?.AnalyticsCookies,
        FunctionalCookies: props.fields?.FunctionalCookies,
        MarketingCookies: props.fields?.MarketingCookies,
    };

    return (
        <GenericHeader
            Id={props.rendering?.uid ?? props.rendering?.componentName}
            AnchorId={props.fields.AnchorId}
            HeaderHeadline={props.fields?.HeaderHeadline}
            HeaderSubHeadline={props.fields?.HeaderSubHeadline}
            HeaderCTA={props.fields?.HeaderCTA}
            headlineSize={props.params?.HeaderHeadlineSize}
            spacingSize={props.params?.Padding}
        >
            {pageEditing && (
                <SalesforceFormIntegrationEditingUI
                    Markup={props.fields?.Markup}
                    ScriptUrl1={props.fields?.ScriptUrl1}
                    ScriptUrl2={props.fields?.ScriptUrl2}
                />
            )}
            <CookieConsent {...cookieConsentFields}>
                <div dangerouslySetInnerHTML={{ __html: html }}></div>
                {scriptUrl1 && <Script src={scriptUrl1}></Script>}
                {scriptUrl2 && <Script src={scriptUrl2}></Script>}
            </CookieConsent>
        </GenericHeader>
    );
};

export const FullWidth = (props: SalesforceFormIntegrationProps) => {
    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();

    const scriptUrl1 = props.fields?.ScriptUrl1?.value;
    const scriptUrl2 = props.fields?.ScriptUrl2?.value;
    const html = props.fields?.Markup?.value;

    const cookieConsentFields = {
        AnalyticsCookies: props.fields?.AnalyticsCookies,
        FunctionalCookies: props.fields?.FunctionalCookies,
        MarketingCookies: props.fields?.MarketingCookies,
    };

    // Track which scripts have loaded
    const scriptsLoaded = useRef({ script1: false, script2: false });

    const initializeSalesforceForm = () => {
        // Only initialize when both scripts are loaded
        if (scriptsLoaded.current.script1 && scriptsLoaded.current.script2) {
            if (window.sfdc_forms) {
                window.sfdc_forms.init();
            } else {
                window.sfdc_forms = { loReady: true };
            }
        }
    };

    // TODO: Insert domain when we get it.
    // const isSalesforceScript = scriptUrl1?.includes(".js") || scriptUrl2?.includes(".js");

    return (
        <>
            {pageEditing && (
                <SalesforceFormIntegrationEditingUI
                    Markup={props.fields?.Markup}
                    ScriptUrl1={props.fields?.ScriptUrl1}
                    ScriptUrl2={props.fields?.ScriptUrl2}
                />
            )}
            <CookieConsent {...cookieConsentFields}>
                <div dangerouslySetInnerHTML={{ __html: html }}></div>
                {scriptUrl1 && scriptUrl1.includes(".js") && (
                    <Script
                        strategy="worker"
                        type="text/javascript"
                        src={scriptUrl1}
                        async
                        // {...(isSalesforceScript && {
                        //     "data-ceros-origin-domains": "my.salesforce.domain",
                        // })}
                        onLoad={() => {
                            scriptsLoaded.current.script1 = true;
                            initializeSalesforceForm();
                        }}
                    />
                )}
                {scriptUrl2 && scriptUrl2.includes(".js") && (
                    <Script
                        strategy="worker"
                        type="text/javascript"
                        src={scriptUrl2}
                        async
                        // {...(isSalesforceScript && {
                        //     "data-ceros-origin-domains": "my.salesforce.domain",
                        // })}
                        onLoad={() => {
                            scriptsLoaded.current.script2 = true;
                            initializeSalesforceForm();
                        }}
                    />
                )}
            </CookieConsent>
        </>
    );
};

export default SalesforceFormIntegration;
